import { useCallback, useMemo } from "react";
import BrandDropdown from "@/components/common/BrandDropdown";
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/field";
import Autocomplete from "@/components/ui/molecules/autocomplete/Autocomplete";
import type { AutocompleteOption } from "@/components/ui/molecules/autocomplete/Autocomplete.type";
import { JollyTextField } from "@/components/ui/textfield";
import type { NewsAlertSortOrder } from "@/data-access/news-alerts/queries/news-alerts-queries";
import { useGetSavedViewsList } from "@/hooks/useGetSavedViewsList";
import {
  closeCreateNewsAlertSectionModal,
  createNewsAlertSection,
  setCreateEditNewsAlertSection,
  updateNewsAlertSection,
} from "@/store/news-alerts/news-alerts.actions";
import useNewsAlertsStore, {
  type AlertSection,
} from "@/store/news-alerts/news-alerts.slice";

const DefaultAlertSection: AlertSection = {
  id: "",
  name: "",
  feedId: null,
  savedViewId: null,
  articleLimit: 25,
  sortOrder: "DATE_DESC" as NewsAlertSortOrder,
};

const CreateEditNewsAlertSectionModal = () => {
  const isOpen = useNewsAlertsStore(
    (state) => state.isCreateNewsAlertSectionModalOpen,
  );
  const isCreatingNewsAlertSection = useNewsAlertsStore(
    (state) => state.isCreatingNewsAlertSection,
  );

  const createEditNewsAlertSection = useNewsAlertsStore(
    (state) => state.createEditNewsAlertSection,
  );

  // Use the hook to fetch saved views based on the selected feedId
  const { items: savedViews, isLoading: isFetchingSavedViews } =
    useGetSavedViewsList({
      feedId: createEditNewsAlertSection.feedId ?? undefined,
    });

  // Convert saved views to AutocompleteOption format
  const savedViewOptions: AutocompleteOption[] = useMemo(
    () =>
      savedViews.map((view) => ({
        value: view.id,
        label: view.name,
      })),
    [savedViews],
  );

  const isEditingNewsAlertSection = useNewsAlertsStore(
    (state) => state.isEditingNewsAlertSection,
  );

  const handleCreateNewsAlertSection = useCallback(() => {
    if (isEditingNewsAlertSection) {
      updateNewsAlertSection(createEditNewsAlertSection);
    } else {
      createNewsAlertSection(createEditNewsAlertSection);
    }
    setCreateEditNewsAlertSection(DefaultAlertSection);
  }, [createEditNewsAlertSection, isEditingNewsAlertSection]);

  const isFormValid = Boolean(
    createEditNewsAlertSection.name.trim() &&
      createEditNewsAlertSection.feedId &&
      createEditNewsAlertSection.savedViewId,
  );

  return (
    <DialogOverlay
      isOpen={isOpen}
      aria-labelledby={"Create Or Edit Brands/Key Topic segment"}
    >
      <DialogContent size="default" onClose={closeCreateNewsAlertSectionModal}>
        <DialogHeader
          title={isEditingNewsAlertSection ? "Edit section" : "Add a section"}
        />
        <DialogBody>
          <div className="flex flex-col gap-4">
            <Label
              aria-label="section-name-label"
              variant={"secondary"}
              size={"sm"}
              className="text-gray-600"
            >
              Add a section name and select a Brand/Topic to add a saved view to
              the alert.
            </Label>
            <JollyTextField
              size={"sm"}
              label={"Section Name"}
              labelVariant={"secondary"}
              placeholder={'ex "Brand name + Environmental Impact"'}
              value={createEditNewsAlertSection.name}
              onChange={(value) =>
                setCreateEditNewsAlertSection({
                  ...createEditNewsAlertSection,
                  name: value,
                })
              }
              isDisabled={isCreatingNewsAlertSection}
              required={createEditNewsAlertSection.name.length === 0}
            />

            <div className="flex flex-col gap-0.5">
              <Label aria-label="brand-label" variant={"secondary"} size={"sm"}>
                Brand/Topic*
              </Label>
              <BrandDropdown
                feedId={createEditNewsAlertSection.feedId}
                setFeedId={(value) =>
                  setCreateEditNewsAlertSection({
                    ...createEditNewsAlertSection,
                    feedId: value,
                    savedViewId: null,
                  })
                }
              />
            </div>
            {createEditNewsAlertSection.feedId && (
              <Autocomplete
                clearable={true}
                label="Saved View*"
                labelVariant="secondary"
                labelSize="sm"
                options={savedViewOptions}
                onValueChange={(value) =>
                  setCreateEditNewsAlertSection({
                    ...createEditNewsAlertSection,
                    savedViewId: value as string | null,
                  })
                }
                value={createEditNewsAlertSection.savedViewId}
                placeholder="Type or select a saved view"
                disabled={!createEditNewsAlertSection.feedId}
                emptyMessage="No saved views found for this brand"
                isPending={isFetchingSavedViews}
                noneOptionText="No saved views found for this brand/topic"
                defaultToNone={false}
              />
            )}
          </div>
        </DialogBody>
        <DialogFooter
          primaryLabel={
            isEditingNewsAlertSection ? "Update section" : "Create section"
          }
          secondaryLabel={"Cancel"}
          secondaryAction={closeCreateNewsAlertSectionModal}
          primaryAction={handleCreateNewsAlertSection}
          primaryDisabled={!isFormValid || isCreatingNewsAlertSection}
          isPending={isCreatingNewsAlertSection}
        />
      </DialogContent>
    </DialogOverlay>
  );
};

export default CreateEditNewsAlertSectionModal;
